//
//  Vatom Inc. Copyright (c) 2023, all rights reserved.
//
//  Licensed under the VatomInc SDK License (the "License"); you may not use this file or
//  the Wallet SDK except in compliance with the License accompanying it. Unless
//  required by applicable law or agreed to in writing, the VatomInc SDK distributed under
//  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF
//  ANY KIND, either express or implied. See the License for the specific language
//  governing permissions and limitations under the License.
//

// import { BusinessSnapshot } from '@vatom/sdk/core'
// import { PageConfig } from '@vatom/sdk/react'
import type { AppConfig, AppConfigInput } from '@vatom/models'
import { AppConfigurationSchema } from '@vatom/models'
import type { AppRoutes } from '@vatom/wallet-routes'
import jwtDecode from 'jwt-decode'
import { v4 as uuidV4 } from 'uuid'

import type { Action } from './CommonActions'
import type { ViewMessagesKey } from './useVatomWalletSdkStore'
import { useVatomWalletSdkStore } from './useVatomWalletSdkStore'

export * from './useVatomWalletSdkStore'
export * from '@vatom/wallet-routes'

export type TabRoute = 'Home' | 'Wallet' | 'Map' | 'MapAr' | 'Connect'

export interface scanner {
  enabled: boolean
}

const defaultConfig = {
  baseUrl: 'https://wallet.vatom.com'
}

export type WalletScreensConfig = {
  [AppRoutes.Room]: {
    hideBackButton?: boolean
  }
  [AppRoutes.CommunitiesRoom]: {
    hideBackButton?: boolean
  }
}

export interface VatomConfig {
  visibleTabs?: string[]
  pageConfig?: PageConfig
  baseUrl?: string
  path?: string
  language?: string
  scanner?: scanner
  mapStyle?: any
  emptyStateImage?: string
  emptyStateTitle?: string
  emptyStateMessage?: string
  screensConfig?: WalletScreensConfig
  systemThemeOverride?: 'light' | 'dark' | null | undefined
  hideDrawer?: boolean
  hideNavigation?: boolean
  hideTokenActions?: boolean
  disableArPickup?: boolean
  disableNewTokenToast?: boolean
  walletConfig?: AppConfigInput
}

type DecodedToken = {
  aud: string
  exp: number
  iat: number
  scope: string
}

export type WalletSdkMessages = {
  // TODO CHANGE THIS TO BE MORE SPECIFIC
  'walletsdk:getPublicProfile': {
    payload: { userId?: string }
    response: string
  }
  'walletsdk:getCurrentUserPoints': {
    payload: { campaignId: string }
    response: string
  }
  'walletsdk:isLoggedIn': { payload?: undefined; response: { loggedIn: boolean } }
  'walletsdk:getCurrentUser': { payload?: undefined; response: string }
  'walletsdk:performAction': {
    payload: {
      tokenId: string
      actionName: string // TODO: fix token.performAction to contain the union of possible actionNames
      actionPayload: any // TODO: fix token.performAction to contain the union of possible actions and their payloads
    }
    response: string
  }
  'walletsdk:combineToken': {
    payload: { thisTokenId: string; otherTokenId: string }
    response: string
  }
  'walletsdk:trashToken': { payload: { tokenId: string }; response: string }
  'walletsdk:getToken': { payload: { tokenId: string }; response: string }
  'walletsdk:getPublicToken': { payload: { tokenId: string }; response: string }
  'walletsdk:listTokens': { payload?: undefined; response: string }
  'wallet-sdk-init': {
    payload: {
      accessToken?: string
      config?: VatomConfig | undefined
      businessId?: string
      walletConfig?: AppConfig
      hostname?: string
      embeddedType?: string
      refreshToken?: string
    }
    response?: undefined
  }
  // remove
  'vatomwallet:SendUsersToWallet': {
    payload?: undefined
    response: void
  }
  // 'vatomwallet:listActionHandlers': {
  //   payload: { vatom: string }
  //   response: {
  //     overrideDropAction: boolean
  //   }
  // }
  'vatomwallet:handleAction': {
    payload: { action: 'Drop'; vatomID: string } // | {action: "OtherAction", withDifferentPayload: string}
    response: void
  }
  'vatomwallet:loaded': {
    payload?: undefined
    response: void
  }
  'vatomwallet:navIsReady': {
    payload?: undefined
    response: void
  }
  'vatomwallet:getCurrentPosition': {
    payload?: undefined
    response: void
  }
  'locationManager:getCurrentPosition': {
    payload?: any
    response: boolean
  }
  'vatomwallet:pre-init': {
    payload?: undefined
    response: void
  }
  /**
   * @deprecated The method should not be used
   */
  'walletsdk:getBusinessTabs': {
    payload?: undefined
    response: string
  }
  /**
   * @deprecated The method should not be used
   */
  'walletsdk:navigateToTab': {
    payload: { route: TabRoute; params?: any }
    response: string
  }
  /**
   * @deprecated The method should not be used
   */
  'walletsdk:navigate': {
    payload: { route: string; params?: any }
    response: string
  }
  'walletsdk:logOut': {
    payload: undefined
    response: string
  }
  'walletsdk:inventoryWasUpdated': {
    payload: undefined
    response: string
  }
  /**
   * @deprecated The method should not be used
   */
  'walletsdk:openCommunity': {
    payload: {
      bussinesId: string
      communityId?: string
      spaceId?: string | undefined
    }
    response: string
  }

  'walletsdk:navigationIsReady': {
    payload: undefined
    response: boolean
  }

  'walletsdk:dispatchNavigationAction': {
    payload: {
      action: Action
    }
    response: void
  }

  'walletsdk:linkTo': {
    payload: {
      url: string
    }
    response: void
  }

  'walletsdk:sendCustomAction': {
    payload: {
      action: string
      token: string
      user: string
      tokenMetadata: string
    }
    response: void
  }

  'walletsdk:faceMessage': {
    payload: {
      token: any
      name: string
      payload: any
    }
    response: void
  }
  'walletsdk:analytics': {
    payload: {
      token?: any
      name?: string
      payload?: any
    }
    response: void
  }

  //...add more messages here
}

export type SDKEventHandler<TMessage extends keyof WalletSdkMessages> = (
  e: WalletSdkMessages[TMessage]['payload']
) => void

//
// This class handles interaction with a parent/child app, if this or that app has been embedded into another app via an iframe or web view.
// This class should be exactly the same on both apps.
//
// Usage for host app: var manager = new VatomWallet(iframeElement.contentWindow) (single instance)
// Usage for child app: VatomWallet.setup() (global instance)
export class VatomWallet {
  static _shared: VatomWallet

  /** Singleton instance */
  static get shared() {
    // Stop if not setup yet
    if (!this._shared) throw new Error('Please call VatomWallet.setup() first.')
    else return this._shared
  }

  /** Get if wallet is embedded or not */
  static isEmbedded() {
    // let isEmbedded =
    //   (window.parent && window.parent !== window) ||
    //   sessionStorage?.getItem('isEmbedded') === 'true' ||
    //   sessionStorage?.getItem('embeddedType') !== null ||
    //   false

    let isEmbedded =
      (window.parent && window.parent !== window) ||
      useVatomWalletSdkStore.getState().isEmbedded ||
      !!useVatomWalletSdkStore.getState().embeddedType ||
      false

    isEmbedded = JSON.parse(isEmbedded.toString().toLowerCase())
    return isEmbedded
  }

  static getEmbeddedType() {
    // const embeddedType = sessionStorage?.getItem('embeddedType') || ''
    const embeddedType = useVatomWalletSdkStore.getState().embeddedType || ''

    return embeddedType
  }

  static async getVatomAccessToken() {
    try {
      // const vatomAccessToken = sessionStorage?.getItem('VATOM_ACCESS_TOKEN') || false
      // const vatomRefreshToken = sessionStorage?.getItem('VATOM_REFRESH_TOKEN') || false
      let vatomAccessToken = useVatomWalletSdkStore.getState().accessToken || false
      const vatomRefreshToken = useVatomWalletSdkStore.getState().refreshToken || false

      if (!vatomAccessToken) {
        return false
      }

      const decodedToken = jwtDecode<DecodedToken>(vatomAccessToken)

      if (decodedToken.aud === 'wallet-rn') {
        return {
          accessToken: vatomAccessToken,
          refreshToken: vatomRefreshToken,
          expiresIn: decodedToken.exp - Math.round(Date.now() / 1000),
          issuedAt: decodedToken.iat,
          tokenType: 'Bearer',
          scope: decodedToken.scope
        }
      }

      const tokenIsExpired = decodedToken.exp < Date.now() / 1000

      if (tokenIsExpired && !vatomRefreshToken) {
        console.error('Token is expired and no refresh token is available')
        return false
      }

      if (tokenIsExpired && vatomRefreshToken) {
        const refreshPayload = new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: decodedToken.aud,
          refresh_token: vatomRefreshToken
        })
        const res = await fetch('https://id.vatom.com/token', {
          method: 'POST',
          headers: { 'content-type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: refreshPayload
        })
        const data = await res.json()
        vatomAccessToken = data.access_token as string
      }

      const urlencoded = new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        client_id: decodedToken.aud,
        resource: 'wallet-rn',
        subject_token_type: 'urn:ietf:params:oauth:token-type:access_token',
        subject_token: vatomAccessToken
      })

      const res = await fetch('https://id.vatom.com/token', {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: urlencoded
      })

      const data = await res.json()
      const exp = data.expires_in || data.expiresIn

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: exp,
        issuedAt: decodedToken.iat,
        tokenType: 'Bearer',
        scope: decodedToken.scope
      }
    } catch (error) {
      console.error('getVatomAccessToken', error)
      return undefined
    }
  }

  static getBusinessId() {
    try {
      // const businessId = sessionStorage?.getItem('VATOM_BUSINESS_ID') || false
      const businessId = useVatomWalletSdkStore.getState().businessId || false

      console.log('CONSOLE.businessId: ', businessId)

      return businessId
    } catch (error) {
      return false
    }
  }

  static SDKDrawerHidden() {
    try {
      if (!useVatomWalletSdkStore.getState().isEmbedded) {
        return false
      }
      // const config = sessionStorage?.getItem('VATOM_CONFIG') || false
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return true
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      if (vatomConfig?.hideDrawer === null || vatomConfig?.hideDrawer === undefined) {
        return true
      }

      return vatomConfig?.hideDrawer
    } catch (error) {
      return true
    }
  }

  static SDKMaxDistanceForPickup() {
    try {
      if (!useVatomWalletSdkStore.getState().isEmbedded) {
        return 0
      }
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return 0
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      if (
        vatomConfig?.pageConfig?.features?.maxDistanceForPickup === null ||
        vatomConfig?.pageConfig?.features?.maxDistanceForPickup === undefined
      ) {
        return 0
      }

      return vatomConfig?.pageConfig?.features?.maxDistanceForPickup
    } catch (error) {
      return 0
    }
  }

  static SDKdisableArPickup() {
    try {
      if (!useVatomWalletSdkStore.getState().isEmbedded) {
        return false
      }
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return true
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      if (vatomConfig?.disableArPickup === null || vatomConfig?.disableArPickup === undefined) {
        return true
      }

      return vatomConfig?.disableArPickup
    } catch (error) {
      return true
    }
  }

  static SDKNavigationHidden() {
    try {
      if (!useVatomWalletSdkStore.getState().isEmbedded) {
        return false
      }
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return true
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      if (vatomConfig?.hideNavigation === null || vatomConfig?.hideNavigation === undefined) {
        return true
      }

      return vatomConfig?.hideNavigation
    } catch (error) {
      return true
    }
  }

  static getTabConfigs(): string[] {
    try {
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return []
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      return vatomConfig?.visibleTabs ?? []
    } catch (error) {
      return []
    }
  }

  static getMapStyles() {
    try {
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return null
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      return vatomConfig?.mapStyle ?? null
    } catch (error) {
      return null
    }
  }

  static getLanguage() {
    try {
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return 'en'
      }

      return vatomConfig?.language || 'en'
    } catch (error) {
      return 'en'
    }
  }

  static tokenActionsDisabled() {
    try {
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return false
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      return !!vatomConfig?.hideTokenActions
    } catch (error) {
      return false
    }
  }

  static getScannerConfig() {
    try {
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return false
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig
      return vatomConfig?.scanner || { enabled: true }
    } catch (error) {
      return false
    }
  }

  static getSDKPageConfig() {
    try {
      const vatomConfig = useVatomWalletSdkStore.getState().config || false

      if (!vatomConfig) {
        return false
      }

      // const vatomConfig = JSON.parse(config) as VatomConfig

      return vatomConfig?.pageConfig ?? false
    } catch (error) {
      return false
    }
  }

  /** Setup the shared instance */
  static setup(elem?: HTMLDivElement) {
    // Stop if setup already
    if (this._shared) return

    // Set it up
    this._shared = new VatomWallet(elem)
  }

  static isRedirectedToBusiness() {
    return this._shared.redirectedToBusiness
  }

  static setRedirectedToBusiness(redirected: boolean) {
    this._shared.redirectedToBusiness = redirected
  }

  /** @private Pending promises */
  pending: {
    [key: string]: {
      resolve: (value: WalletSdkMessages[keyof WalletSdkMessages]['payload']) => void
      reject: (reason?: string | Error) => void
      timer: NodeJS.Timeout
    }
  } = {}

  /** Register handlers */
  handlers: { [key in keyof WalletSdkMessages]?: SDKEventHandler<key> } = {}

  /** The "window" to post messages to */
  hostWindow?: Window

  businessId?: string
  accessToken?: string
  // TODO: change config to be typed
  config?: VatomConfig
  redirectedToBusiness = false

  /** Constructor */
  constructor(
    elem: HTMLDivElement | undefined = undefined,
    accessToken = '',
    businessId = '',
    config?: VatomConfig,
    refreshToken = ''
  ) {
    // Setup message listener
    window.addEventListener('message', (event: MessageEvent) => {
      this.onMessage(event)
    })

    useVatomWalletSdkStore.subscribe(state => {
      if (state.inventoryWasUpdated) {
        this.sendMsgWithoutResponse('walletsdk:inventoryWasUpdated')
        this.sendAnalytics('walletsdk:inventoryWasUpdated', {})
      }
    })

    // Store vars
    console.log('[Vatom VatomWallet] Mode: ' + (elem ? 'host container' : 'embedded'))
    this.businessId = businessId
    this.accessToken = accessToken
    this.config = config

    let iframe: HTMLIFrameElement | undefined = undefined
    if (elem) {
      this.handle('vatomwallet:pre-init', () => {
        const walletConfig = AppConfigurationSchema.parse(config?.walletConfig ?? {})

        this.sendMsgWithoutResponse('wallet-sdk-init', {
          accessToken,
          config,
          walletConfig: walletConfig,
          hostname: window.location.hostname,
          refreshToken
        })
        return true
      })
      iframe = document.createElement('iframe')
      iframe.setAttribute(
        'allow',
        'geolocation *; accelerometer *; gyroscope *; magnetometer *; ambient-light-sensor *; camera *; microphone *;'
      )
      // const script = iframe.contentDocument.document.createElement('script')
      // script.type = 'text/javascript'
      // script.src = `
      //   sessionStorage?.setItem('isEmbedded', 'true');
      //   sessionStorage?.setItem('VATOM_BUSINESS_ID', '${businessId}');
      //   sessionStorage?.setItem('VATOM_ACCESS_TOKEN', ${neededForAuth});
      //   sessionStorage?.setItem('VATOM_CONFIG', ${config});
      // `
      // iframe.contentDocument.document.body.appendChild(script)
      let src = `${config?.baseUrl ?? defaultConfig.baseUrl}`

      if (this.businessId) {
        console.warn(
          'Business ID was passed trough config, this is deprecated and will be removed in the future. Please pass the path you intend to use in the config to target the expected initial route. i.e. /b/:businessId  /b/:businessId/map '
        )

        if (!this.config?.path) {
          src += `/b/${businessId}`
        } else {
          console.warn(
            'Both businessId and path were passed, the path will be used instead of the businessId'
          )
        }
      }

      if (this.config?.path) {
        src += this.config.path
      }

      iframe.src = src
      iframe.style.height = '100%'
      iframe.style.width = '100%'
      iframe.style.border = 'none'
      elem.replaceChildren(iframe)
    }

    this.hostWindow = iframe?.contentWindow || window.opener || window.parent
    const isInIframe = window.parent && window.parent !== window
    const isInPopup = window.opener && window.opener !== window

    // console.log('isInIframe : ', isInIframe)
    // console.log('isInIPopup : ', isInPopup)
    // console.log('isEmbedded : ', isEmbedded)

    if (!VatomWallet.isEmbedded()) {
      sessionStorage?.setItem('isEmbedded', isInIframe.toString())
      useVatomWalletSdkStore.setState({ isEmbedded: isInIframe })
    }
    if (isInPopup) {
      try {
        const hostName = window.opener?.location?.hostname || window.opener?.location?.host
        sessionStorage?.setItem('popupHostName', hostName)
        useVatomWalletSdkStore.getState().setPopupHostName(hostName)
      } catch (e) {
        console.warn('Opened from cross-domain, could not find host name')
        sessionStorage?.setItem('popupHostName', '')
        useVatomWalletSdkStore.getState().setPopupHostName('')
      }
    }
    if (!elem) {
      this.preInit()
    }

    this.handle('vatomwallet:SendUsersToWallet', async e => {
      // Get Users from vatom
      // localStorage.setItem('ui.vatom-users', JSON.stringify(e.data.payload.users))
      // localStorage.setItem('isMobile', e.data.payload.isMobile)
      console.log('send message across')
    })
  }

  // Function to check if emebedded other than web
  preInit() {
    this.handle('wallet-sdk-init', this.handleInit)
    const target =
      (window as any).webkit?.messageHandlers?.vatomMessageHandler ||
      (window as any).vatomMessageHandler
    this.sendMsgWithoutResponse('vatomwallet:pre-init', undefined, target)
  }

  handleInit: SDKEventHandler<'wallet-sdk-init'> = e => {
    console.log('inititating init on client with data:', e)
    // // TODO: save access token, call login, etc.
    // const neededForAuth = {
    //   accessToken: e.accessToken,
    //   refreshToken: e.refreshToken,
    //   idToken: e.idToken
    // }
    sessionStorage?.setItem('VATOM_ACCESS_TOKEN', e.accessToken || '')
    sessionStorage?.setItem('VATOM_REFRESH_TOKEN', e.refreshToken || '')
    sessionStorage?.setItem('VATOM_CONFIG', JSON.stringify(e.config))
    sessionStorage?.setItem('isEmbedded', 'true')
    sessionStorage?.setItem('VATOM_BUSINESS_ID', e.businessId || '')
    sessionStorage?.setItem('POPUP_HOSTNAME', e.hostname || '')

    const initState = useVatomWalletSdkStore.getState().config

    useVatomWalletSdkStore.setState({
      config: {
        ...initState,
        ...e.config,
        hideNavigation: e.config?.hideNavigation ?? initState.hideNavigation,
        hideDrawer: e.config?.hideDrawer ?? initState.hideDrawer,
        disableArPickup: e.config?.disableArPickup ?? initState.disableArPickup,
        hideTokenActions: e.config?.hideTokenActions ?? initState.hideTokenActions
      },
      accessToken: e.accessToken,
      refreshToken: e.refreshToken,
      isEmbedded: true,
      businessId: e.businessId,
      popupHostName: e.hostname,
      walletConfig: e.walletConfig
    })

    if (e.embeddedType) {
      sessionStorage?.setItem('embeddedType', e.embeddedType)
      useVatomWalletSdkStore.setState({ embeddedType: e.embeddedType })

      if (e.embeddedType.includes('ios')) {
        navigator.geolocation.getCurrentPosition = this.getGeolocator(
          e.embeddedType
        ).getCurrentPosition
        navigator.geolocation.clearWatch = this.getGeolocator(e.embeddedType).clearWatch
        navigator.geolocation.watchPosition = this.getGeolocator(e.embeddedType).watchPosition
      }
    }

    VatomWallet._shared.sendMsgWithoutResponse('vatomwallet:loaded')
    window.dispatchEvent(new Event('vatomwallet:loaded'))

    return true
  }

  /** @private Send a raw payload to the other app */
  sendPayload(payload: any, target?: any) {
    // Send message to parent frame
    // TODO: Figure out if this is a security risk to use the "*" origin. It would allow anyone to embed the viewer inside
    // an iframe and then extract the user's refresh token after they login... Maybe this should instead be controlled
    // through a a list of whitelisted origins.
    try {
      console.log('[Vatom VatomWallet] Sending payload to other side: ', payload)
      console.log('[Vatom VatomWallet] getEmbeddedType: ', VatomWallet.getEmbeddedType())

      // const embeddedType = VatomWallet.getEmbeddedType()
      const embeddedType = useVatomWalletSdkStore.getState().embeddedType
      let channel = target

      switch (embeddedType) {
        case 'flutter-ios':
        case 'flutter-android':
          channel = (window as any).vatomMessageHandler
          break
        case 'ios':
          channel = (window as any).webkit.messageHandlers.vatomMessageHandler
          break
      }

      if (channel) {
        return channel.postMessage(JSON.stringify(payload))
      }
      if (this.hostWindow && this.hostWindow != window)
        return this.hostWindow.postMessage(payload, '*')
      // Not in a host app
      // throw new Error("Not inside a host app.");
    } catch (error) {
      console.log('error on sendPayload: ', error)
    }
  }

  // async getDefaultConfig() {
  //   return fetch('https://resources.vatom.com/system/defaults/walletsdk.json')
  //     .then(res => res.json())
  //     .then(result => result)
  // }

  // Function overload that looks at the message name and payload to determine the return type while still allowing not passing a payload for those messages that don't need it
  async sendMsg<
    TMessage extends {
      [K in keyof WalletSdkMessages]: WalletSdkMessages[K]['payload'] extends undefined ? K : never
    }[keyof WalletSdkMessages]
  >(name: TMessage): Promise<WalletSdkMessages[TMessage]['response']>
  async sendMsg<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload: WalletSdkMessages[TMessage]['payload'],
    target?: any,
    time?: number
  ): Promise<WalletSdkMessages[TMessage]['response']>

  /** Send a message to the parent app. @returns Promise */
  async sendMsg<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload?: WalletSdkMessages[TMessage]['payload'],
    target?: any,
    time?: number
  ) {
    // Create unique ID for this request
    const id = uuidV4()

    // Send payload
    this.sendPayload({ id, name, payload, request: true }, target)

    // Create pending promise
    return new Promise<WalletSdkMessages[TMessage]['payload']>((resolve, reject) => {
      // Create timeout timer
      const timer = setTimeout(() => {
        // Fail it
        this.pending[id].reject(new Error(`Timed out waiting for response. ${name} ${id}`))
        delete this.pending[id]
      }, time || 15000)

      // Store pending promise
      this.pending[id] = { resolve, reject, timer }
    })
  }

  sendAnalytics(name: string, payload: any, token?: any) {
    this.sendMsgWithoutResponse('walletsdk:analytics', {
      name: name,
      payload: payload,
      token: token ?? {}
    })
  }

  sendMsgWithoutResponse<
    TMessage extends {
      [K in keyof WalletSdkMessages]: WalletSdkMessages[K]['payload'] extends undefined ? K : never
    }[keyof WalletSdkMessages]
  >(name: TMessage): void
  sendMsgWithoutResponse<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload: WalletSdkMessages[TMessage]['payload'],
    target?: any
  ): void

  /** Send a message and don't care about the response or even if it's received */
  sendMsgWithoutResponse<TMessage extends keyof WalletSdkMessages>(
    name: TMessage,
    payload?: WalletSdkMessages[TMessage]['payload'],
    target?: any
  ) {
    // Send it
    this.sendMsg(name, payload, target).catch(err => {
      // Ignore error
      // console.log('[Vatom VatomWallet] Response error ignored. ' + err.message)
    })
  }

  /** @private Called when a message is received */
  onMessage = <TMessage extends keyof WalletSdkMessages>(e: {
    data: {
      id: string
      name: TMessage
      request: boolean
      payload: WalletSdkMessages[TMessage]['payload']
      error?: string | undefined
    }
  }) => {
    // Extract params
    const { id, request, name, payload, error } = e.data

    // No ID given in message
    if (!id) {
      return
    }

    // Check if request or response
    if (request) {
      // Find handler and run it
      Promise.resolve()
        .then(e => {
          // Find handler
          const handler = this.handlers[name]
          if (!handler) throw new Error('No handler registered for ' + name)

          // Call it
          return handler(payload)
        })
        .then(payload => {
          // Send success response
          this.sendPayload({ id, payload, request: false })
        })
        .catch(err => {
          // Send failure response
          this.sendPayload({ id, error: err.message, request: false })
        })
    } else {
      // Response, find pending promise
      if (!this.pending[id]) {
        // Not found
        console.warn('[Vatom VatomWallet] No pending request for ID', id)
        return
      }

      // Resolve the pending promise
      if (error) this.pending[id].reject(new Error(error))
      else this.pending[id].resolve(payload)

      // Remove pending promise
      clearTimeout(this.pending[id].timer)
      delete this.pending[id]
    }
  }

  /** Register a handler function to handle a certain message */
  handle<TMessage extends keyof WalletSdkMessages, TCallBack extends SDKEventHandler<TMessage>>(
    name: TMessage,
    callback: TCallBack
  ) {
    this.handlers[name] = callback
  }

  onClose() {
    window.removeEventListener('message', this.onMessage)
    this.pending = {}
    this.handlers = {}
    delete this.hostWindow
  }

  getGeolocator(type: string) {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const clearWatch = (watchId: number) => {}
    const getCurrentPosition = async (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback | null,
      options?: PositionOptions
    ) => {
      try {
        const { coords } = (await this.sendMsg('vatomwallet:getCurrentPosition', undefined)) as any
        successCallback({ coords: coords, timestamp: new Date().getTime() })
      } catch (error: any) {
        if (errorCallback) {
          errorCallback({
            message: error.message,
            code: 2,
            PERMISSION_DENIED: 1,
            POSITION_UNAVAILABLE: 2,
            TIMEOUT: 3
          })
        }
      }
    }
    const watchPosition = (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback | null,
      options?: PositionOptions
    ) => {
      return 0
    }

    return {
      clearWatch,
      getCurrentPosition,
      watchPosition
    }
  }

  // Function to be called by the host to perform an action on a token
  async performAction(tokenId: string, actionName: string, payload?: any) {
    return JSON.parse(
      await this.sendMsg('walletsdk:performAction', {
        tokenId,
        actionName,
        actionPayload: payload
      })
    )
  }
  // Function to be called by the host to combine tokens
  async combineTokens(thisTokenId: string, otherTokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:combineToken', { thisTokenId, otherTokenId }))
  }
  // Function to be called by the host to trash a token
  async trashToken(tokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:trashToken', { tokenId }))
  }
  // Function to be called by the host to get a token
  async getToken(tokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:getToken', { tokenId }))
  }
  // Function to be called by the host to get a public token
  async getPublicToken(tokenId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:getPublicToken', { tokenId }))
  }
  // Function to be called by the host to get the list of tokens the user owns
  async listTokens() {
    return JSON.parse(await this.sendMsg('walletsdk:listTokens'))
  }

  async isLoggedIn() {
    return await this.sendMsgWithoutResponse('walletsdk:isLoggedIn')
  }

  async getPublicProfile(userId?: string) {
    return JSON.parse(await this.sendMsg('walletsdk:getPublicProfile', { userId }))
  }

  async getCurrentUserPoints(campaignId: string) {
    return JSON.parse(await this.sendMsg('walletsdk:getCurrentUserPoints', { campaignId }))
  }

  async getCurrentUser() {
    return JSON.parse(await this.sendMsg('walletsdk:getCurrentUser'))
  }

  async getBusinessTabs() {
    return JSON.parse(await this.sendMsg('walletsdk:getBusinessTabs'))
  }

  isNavigationReady() {
    const message = this.sendMsg('walletsdk:navigationIsReady', undefined, undefined, 250)
      .then(res => {
        return !!res
      })
      .catch(err => {
        console.log('isNavigationReady', err)
        return false
      })

    return message
  }
  /**
   * @deprecated The method should not be used
   */
  async navigateToTab(tab: TabRoute, p?: any) {
    const params = { ...p, businessId: this.businessId }
    return await this.sendMsgWithoutResponse('walletsdk:navigateToTab', {
      route: tab,
      params
    })
  }
  /**
   * @deprecated The method should not be used
   */
  async navigate(route: string, p?: any) {
    const params = { ...p, businessId: this.businessId }
    return await this.sendMsgWithoutResponse('walletsdk:navigate', { route: route, params: params })
  }
  async logOut() {
    await this.sendMsgWithoutResponse('walletsdk:logOut')
    return {}
  }

  async linkTo(url: string) {
    return await this.sendMsg('walletsdk:linkTo', { url })
  }

  async dispatchNavigationAction(action: Action) {
    return await this.sendMsgWithoutResponse('walletsdk:dispatchNavigationAction', { action })
  }

  /**
   * @deprecated The method should not be used
   */ async openCommunity(bussinesId: string, communityId?: string, spaceId?: string | undefined) {
    return await this.sendMsgWithoutResponse('walletsdk:openCommunity', {
      bussinesId,
      communityId,
      spaceId
    })
  }

  on(message: ViewMessagesKey, callback: (data: unknown) => void) {
    const messageHandlers = useVatomWalletSdkStore.getState().viewMessageHandlers || []

    const handler = messageHandlers[message]

    if (!handler) {
      useVatomWalletSdkStore.setState({
        viewMessageHandlers: {
          ...messageHandlers,
          [message]: callback
        }
      })
    }
  }
}
interface PageThemeHeader {
  logo: string
}

interface PageThemeIconTitle {
  // Define properties if needed
}

interface PageThemeIcon {
  // Define properties if needed
}

interface PageThemeMain {
  // Define properties if needed
}

interface PageThemeEmptyState {
  // Define properties if needed
}

interface PageTheme {
  header: PageThemeHeader
  iconTitle: PageThemeIconTitle
  icon: PageThemeIcon
  main: PageThemeMain
  emptyState: PageThemeEmptyState
  mode: string
  pageTheme: string
}

interface PageText {
  emptyState: string
}

interface PageFeaturesNotifications {
  // Define properties if needed
}

interface PageFeaturesCard {
  // Define properties if needed
}

interface PageFeaturesFooterIcon {
  id: string
  src?: string
  title: string
}

interface PageFeaturesFooter {
  enabled: boolean
  icons: PageFeaturesFooterIcon[]
}

interface PageFeaturesVatom {
  // Define properties if needed
}

interface PageFeatures {
  notifications?: PageFeaturesNotifications
  card?: PageFeaturesCard
  footer?: PageFeaturesFooter
  vatom?: PageFeaturesVatom
  maxDistanceForPickup?: number
  businessHeader?: {
    hideHeader?: boolean
  }
  inventory?: {
    hideHeader?: boolean
  }
  icon?: {
    badges: boolean
    editions: boolean
    titles: boolean
  }
}

export interface PageConfig {
  theme?: PageTheme
  text?: PageText
  features?: PageFeatures
}
